import type { ObjectToolbarConfig } from '@/blocks/object-toolbar';
import type { ExportOption } from '@/hooks/useExportOptions';

interface BuildRegisterToolbarConfigArgs {
  title: string;
  counter?: string;
  exportOptions: ExportOption[];
  /**
   * Opener + labels for the ribbon's "Edit Filters" modal, bridged up from the
   * page's `CustomisableRibbon` (relocate mode). When present, adds an overflow
   * "Edit" group so the toolbar hosts the ribbon-edit action the ribbon itself no
   * longer renders (CUS-150). Omitted when the user can't manage the ribbon.
   */
  editRibbon?: {
    /** Overflow group heading (e.g. "Edit"). */
    groupLabel: string;
    /** Action label (e.g. "Edit Filters"). */
    label: string;
    onClick: () => void;
  };
}

/**
 * Assemble the object-toolbar config for the Indicators register/list page
 * (CUS-150). Register toolbars are type-level: no `parentObject` (permissions
 * resolve against the type) and no `save` (a list page has no form). The
 * register's multi-format export (CSV / PDF / custom PDF) is surfaced as the
 * toolbar's "Download" group — one action per {@link ExportOption} returned by
 * `useExportOptions`, the same source that drives the legacy `ExportButton`.
 *
 * Pure so it can be unit-tested without rendering the page.
 */
export const buildIndicatorRegisterToolbarConfig = ({
  title,
  counter,
  exportOptions,
  editRibbon,
}: BuildRegisterToolbarConfigArgs): ObjectToolbarConfig => ({
  header: { title, counter },
  actions: {
    ...(editRibbon
      ? {
          overflow: [
            {
              id: 'edit',
              label: editRibbon.groupLabel,
              actions: [
                {
                  id: 'edit-ribbon',
                  label: editRibbon.label,
                  iconName: 'filter-lines',
                  onClick: editRibbon.onClick,
                },
              ],
            },
          ],
        }
      : {}),
    export: [
      {
        id: 'export',
        actions: exportOptions.map((option) => ({
          id: option.id,
          label: option.text,
          iconName: 'download-01',
          onClick: option.exportFunction,
          disabled: option.disabled,
        })),
      },
    ],
  },
});
